import useCustomers from '@/api/accounts-receivable/customers/useCustomers';
import useCustomer from '@/api/accounts-receivable/customers/useCustomer';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { useState, useMemo } from 'react';
import { useGT } from 'gt-react';
import CustomerPill from 'src/components/customer/CustomerPill';

export function CustomersAutocomplete({
  selectedCustomerId,
  onCustomerSelect,
  allowClear = false,
  className,
}: {
  selectedCustomerId: string | undefined;
  onCustomerSelect: (customerId: string | undefined) => void;
  allowClear?: boolean;
  className?: string;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const { data: customersData } = useCustomers({
    search: search || undefined,
  });

  // Fetch the selected customer to ensure it's always available for display
  const { data: selectedCustomerData } = useCustomer(selectedCustomerId);

  // Merge the selected customer into the options if it's not already there
  const options = useMemo(() => {
    const customers = customersData?.edges || [];

    // If there's a selected customer and it's not in the list, add it
    if (
      selectedCustomerData?.customer &&
      !customers.some((c) => c.id === selectedCustomerData.customer.id)
    ) {
      // Convert DetailedCustomerDTO to CustomerPreviewDTO format
      return [
        {
          id: selectedCustomerData.customer.id,
          name: selectedCustomerData.customer.name,
          erpDisplayName: selectedCustomerData.customer.erpDisplayName,
          aliases: selectedCustomerData.customer.aliases,
          address: selectedCustomerData.customer.address,
          phoneNumber: selectedCustomerData.customer.phoneNumber,
          emailDomain: selectedCustomerData.customer.emailDomain,
          contactPerson: selectedCustomerData.customer.contactPerson,
          paymentTerms: selectedCustomerData.customer.paymentTerms,
          creditLimit: selectedCustomerData.customer.creditLimit,
          creditLimitCurrency:
            selectedCustomerData.customer.creditLimitCurrency,
          isaId: selectedCustomerData.customer.isaId,
        },
        ...customers,
      ];
    }

    return customers;
  }, [customersData?.edges, selectedCustomerData]);

  return (
    <Autocomplete
      value={selectedCustomerId}
      getValue={(customer) => customer.id}
      getLabel={(customer) => customer.erpDisplayName ?? customer.name}
      options={options}
      searchValue={search}
      onSearchValueChange={setSearch}
      onValueChange={onCustomerSelect}
      placeholder={gt('Search customers', {
        context: 'customer search input placeholder',
      })}
      multiSelect={false}
      allowClear={allowClear}
      className={className}
      renderTrigger={(customer) => (
        <CustomerPill
          customer={{
            name: customer.erpDisplayName ?? customer.name,
            emailDomain: customer.emailDomain ?? undefined,
            phoneNumber: customer.phoneNumber ?? undefined,
          }}
        />
      )}
    />
  );
}
